import type { AdminRole } from './types'

/**
 * 后端返回的错误体是 `{ error, message, detail }`（见 docs/TECH-api.md 1.2），
 * 成功时直接是数据体，不套壳。这里把这个约定收敛成一个函数，
 * 页面层只需要 try/catch，不用每处都记得 `res.ok` 和解包。
 */
export interface ApiError {
  error: string
  message: string
  detail: unknown
}

export class RequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.message)
  }
}

const TOKEN_KEY = 'admin_token'

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = tokenStore.get()
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  })

  if (res.status === 204) return undefined as T

  const body = await res.json().catch(() => ({
    error: 'INTERNAL_ERROR',
    message: '响应解析失败',
    detail: null,
  }))

  if (!res.ok) {
    // token 过期就地清掉并踢回登录页，不让后续请求继续用一个已死的 token 打
    if (res.status === 401) {
      tokenStore.clear()
      if (location.pathname !== '/login') location.href = '/login'
    }
    throw new RequestError(res.status, body as ApiError)
  }
  return body as T
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown> | object) => {
    const qs = query
      ? '?' +
        new URLSearchParams(
          Object.entries(query)
            .filter(([, v]) => v !== undefined && v !== '' && v !== null)
            .map(([k, v]) => [k, String(v)]),
        )
      : ''
    return request<T>(path + qs)
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

export interface AdminMe {
  sub: string
  username: string
  displayName: string
  role: AdminRole
}
